import { usePaginatedQuery, useUrlPaginatedQuery } from "@shared/hooks";
import type { PaginatedQueryParams } from "@shared/api";
import { axiosGetData } from "@shared/api/clients";
import { useQuery } from "@tanstack/react-query";

import type {
  GetStructureByIdResponse,
  ListStructuresResponse,
  StructureFilters,
} from "./structures.types";

const STRUCTURES_QUERY_KEY = "structures";

const getStructuresEndpoint = (tenantId: string) =>
  `v1/managerial/tenants/${tenantId}/structures`;

export const STRUCTURES_FILTER_KEYS: (keyof StructureFilters)[] = [
  "name",
  "complexName",
  "sortBy",
  "sortOrder",
];

export const getStructureById = async (
  tenantId: string,
  structureId: string,
): Promise<GetStructureByIdResponse> => {
  return axiosGetData<GetStructureByIdResponse>(
    `${getStructuresEndpoint(tenantId)}/${structureId}`,
  );
};

export const useGetStructureByIdQuery = (
  tenantId: string,
  structureId: string,
  options = {},
) => {
  return useQuery({
    queryKey: [STRUCTURES_QUERY_KEY, tenantId, structureId],
    queryFn: () => getStructureById(tenantId, structureId),
    enabled: !!tenantId && !!structureId,
    ...options,
  });
};

export const useListStructuresQuery = (
  tenantId: string,
  params?: PaginatedQueryParams<StructureFilters>,
) => {
  return usePaginatedQuery<ListStructuresResponse, StructureFilters>({
    endpoint: getStructuresEndpoint(tenantId),
    queryKeyBase: [STRUCTURES_QUERY_KEY, tenantId],
    params,
    enabled: !!tenantId,
  });
};

export const useListStructuresUrlQuery = (tenantId: string | null) => {
  return useUrlPaginatedQuery<ListStructuresResponse, StructureFilters>({
    endpoint: getStructuresEndpoint(tenantId ?? ""),
    queryKeyBase: [STRUCTURES_QUERY_KEY, tenantId ?? ""],
    filterKeys: STRUCTURES_FILTER_KEYS,
    enabled: !!tenantId,
  });
};
